/**
 * The Poker room's Play tab — Level 1, the cards.
 *
 * ============================ WHAT THIS REPLACED ===============================
 * `components/TableSetup.tsx`: one screen with eleven controls on it, which is the
 * problem `PRODUCT-FLOW-AND-PRICING.md` §1 names by its right name — the engine's
 * parameter list used as navigation. The file is still in the tree, and nothing routes
 * to it any more; every field it had is on a card here, on Custom Table, or in the
 * Settings sheet, and the report's mapping table names which for each one.
 *
 * The order is the master plan's, and it is not alphabetical or arbitrary: Quick Play
 * is first and big because the commonest thing a player wants is a hand; Custom Table
 * is last and small because the commonest thing a player wants is not a form.
 * ===============================================================================
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { unlock as unlockSound } from "../table/sounds";
import { PresetCard } from "./PresetCard";
import { PLAY_CARDS, TRAIN_CARDS, defaultDials, seatShape, type Dial, type PlayCard } from "./poker-cards";
import { useWallet, BucksAmount } from "../lib/WalletContext";
import { seatCost, stakesLabel, prizePool } from "../lib/wallet-client";
import { seatUrl, savedSeatUrl, type Dials } from "./seat-url";
import { getLastCard, removeSavedCard, setLastCard, usePrefsStore } from "../settings/prefs";
import "./rooms.css";

export function PokerPlay() {
  const navigate = useNavigate();
  const { saved } = usePrefsStore();
  /* What a seat costs is a property of the card; whether you can afford it is a
     property of you, and the card has to say both. */
  const { balance } = useWallet();
  /** Where every card's dials are, keyed by card id. Nothing is stored: it is a choice
      for this visit, and the card it produces can be saved from Custom Table. */
  const [dials, setDials] = useState<Record<string, Dials>>(() =>
    Object.fromEntries(PLAY_CARDS.map((c) => [c.id, defaultDials(c)])));

  const move = (id: string, kind: Dial["kind"], value: number) =>
    setDials((d) => ({ ...d, [id]: { ...d[id], [kind]: value } }));

  const play = (id: string) => {
    setLastCard(id);
    /*
     * PHASE 6: this press is what starts the audio.
     *
     * Browsers only allow an `AudioContext` inside a user gesture, and the first sound
     * a sitting makes is the seat card's own — which arrives two to five seconds later,
     * with no gesture anywhere near it. Taking the card IS the gesture, so it is the
     * one that unlocks. Nothing is played here; `unlock` builds the context and starts
     * the sprite downloading, which is also the moment there is time for it.
     */
    unlockSound();
    navigate(seatUrl(id, dials[id] ?? {}));
  };

  const hero = PLAY_CARDS.filter((c) => c.hero);
  const rest = PLAY_CARDS.filter((c) => !c.hero);

  /**
   * The price on a card.
   *
   * A cash card names its STAKES and what a full buy-in of them costs — "2/5 · 100bb"
   * and 500 Bucks, and the arithmetic between them is `seatCost`, never a number typed
   * beside a stack it is supposed to equal.
   *
   * A tournament card names its BUY-IN and the pool it funds, and it may only do that
   * because these tournaments have no real prize (decision D7). A verifying card has
   * no table behind it and therefore no price.
   */
  const priceOf = (c: PlayCard) => {
    if (c.verifying) return undefined;
    const shape = seatShape(c, dials[c.id] ?? {});
    const buyin = seatCost(shape);
    if (buyin <= 0) return undefined;
    const afford = balance >= buyin;
    const short = afford ? undefined : (
      <>
        Seat <BucksAmount n={buyin} /> · you have <BucksAmount n={balance} />
      </>
    );
    return {
      buyin,
      afford,
      label: "",
      short: short ?? (shape.format === "cash" ? (
        <>{stakesLabel(shape)} · buy in for <BucksAmount n={buyin} /></>
      ) : (
        <>Buy-in <BucksAmount n={buyin} /> · pool <BucksAmount n={prizePool(shape)} /></>
      )),
    };
  };

  return (
    <div className="tab-play">
      <div className="cardgrid">
        {hero.map((c) => (
          <PresetCard key={c.id} title={c.title} line={c.line} hero
                      dials={c.dials} values={dials[c.id]} cost={priceOf(c)}
                      onDial={(k, v) => move(c.id, k, v)}
                      onPlay={() => play(c.id)} />
        ))}
        {rest.map((c) => (
          <PresetCard key={c.id} title={c.title} line={c.line}
                      dials={c.dials} values={dials[c.id]} cost={priceOf(c)}
                      turbo={c.turbo} chip={c.chip}
                      verifying={c.verifying} guardrail={c.guardrail}
                      onDial={(k, v) => move(c.id, k, v)}
                      onPlay={() => play(c.id)} />
        ))}

        {/* Saved cards sit AFTER Cash Table and BEFORE Custom Table, which is where
            they were made and where a player will look for them. */}
        {saved.map((c) => (
          <PresetCard key={c.id} title={c.name} mine
                      line={describe(c.setup as Record<string, unknown>)}
                      to={savedSeatUrl(c.id)}
                      onForget={() => removeSavedCard(c.id)} />
        ))}

        <PresetCard quiet title="Custom Table" action="Open"
                    line="Every dial the engine has, on one screen. Save what you build as a card."
                    to="/poker/play/custom" />
      </div>

      <SharpenThis />
    </div>
  );
}

/** A saved card's one line, built from the setup rather than typed by the player. */
function describe(s: Record<string, unknown>): string {
  const seats = Number(s.seats ?? 6);
  const stack = Number(s.stackBb ?? 100);
  const format = String(s.format ?? "cash");
  return `${seats === 2 ? "Heads-up" : `${seats}-handed`} · ${stack}bb · ${format === "cash" ? "cash" : "tournament"}`;
}

/**
 * "Sharpen this" — the one marketing surface on this tab.
 *
 * The rule from the master plan §7, and every word of it is a constraint: one line, one
 * tap, about what this player was just doing, no countdown, no scarcity, never during a
 * hand. So it sits at the FOOT of the tab, after the player has chosen or not chosen,
 * it names the drill that matches the last card they took, and it says nothing at all
 * to somebody who has not played yet.
 */
function SharpenThis() {
  const last = getLastCard();
  const card = PLAY_CARDS.find((c) => c.id === last);
  const drill = TRAIN_CARDS.find((t) => t.id === (card?.drill ?? "pushfold"));
  if (!card || !drill) return null;
  return (
    <aside className="sharpen">
      <p>
        <b>Sharpen this.</b> {card.title} is where {drill.title.toLowerCase()} pays.
      </p>
      <Link className="btn-small" to="/poker/train">{drill.title}</Link>
    </aside>
  );
}
